import { InvestmentPathDetail } from "../interfaces/DTO/mislaka_xml_models";
import { MimshakData } from "../interfaces/DTO/mimshakData";
import { formatCurrency } from "../utils/namesFormater";

export interface PathIntegrityDetail {
  pathCode: number;
  pathName: string;
  originalAmount: number;
  correctedAmount: number;
}

export interface PolicyIntegrityIssue {
  policyNumber: string;
  expectedAmount: number;
  actualAmount: number;
  coveragePct: number; // actual / expected, 0–1
  pathCount: number;
  fixed: boolean;
  paths: PathIntegrityDetail[];
}

export interface IntegrityCheckResult {
  valid: boolean;
  issues: PolicyIntegrityIssue[];
  /** A copy of data with investment_paths amounts corrected */
  correctedData: MimshakData[];
}

const TOLERANCE = 5; // up to 5 ILS deviation allowed

/**
 * Checks whether investment_paths accumulated_amount totals match accumulated_savings totals
 * for each policy. When they differ:
 *   - Single path: sets its amount to the expected total.
 *   - Multiple paths: scales each path proportionally so they sum to expected.
 * Returns a corrected deep copy of the data and a list of issues found.
 */
export function checkAndFixInvestmentPathAmounts(data: MimshakData[]): IntegrityCheckResult {
  const issues: PolicyIntegrityIssue[] = [];

  const correctedData: MimshakData[] = data.map((row) => {
    const products = row.data?.products ?? [];
    let rowHasIssue = false;

    const correctedProducts = products.map((product) => {
      const correctedPolicies = (product.policies_or_accounts ?? []).map((policy) => {
        const expectedAmount = (policy.accumulated_savings ?? []).reduce(
          (s, a) => s + a.total_savings,
          0
        );

        if (expectedAmount <= 0) return policy;

        const paths: InvestmentPathDetail[] = policy.investment_paths ?? [];
        const actualAmount = paths.reduce((s, p) => s + (p.accumulated_amount ?? 0), 0);

        const diff = Math.abs(actualAmount - expectedAmount);
        if (diff <= TOLERANCE) return policy;

        const coveragePct = expectedAmount > 0 ? actualAmount / expectedAmount : 0;
        const issue: PolicyIntegrityIssue = {
          policyNumber: policy.policy_number,
          expectedAmount,
          actualAmount,
          coveragePct,
          pathCount: paths.length,
          fixed: true,
          paths: [],
        };
        issues.push(issue);
        rowHasIssue = true;

        let correctedPaths: InvestmentPathDetail[];
        if (paths.length === 1) {
          correctedPaths = [{ ...paths[0], accumulated_amount: expectedAmount }];
        } else if (actualAmount > 0) {
          const scale = expectedAmount / actualAmount;
          correctedPaths = paths.map((p) => ({
            ...p,
            accumulated_amount: (p.accumulated_amount ?? 0) * scale,
          }));
        } else {
          const perPath = expectedAmount / paths.length;
          correctedPaths = paths.map((p) => ({ ...p, accumulated_amount: perPath }));
        }

        issue.paths = paths.map((p, i) => ({
          pathCode: p.path_code,
          pathName: p.path_name ?? String(p.path_code),
          originalAmount: p.accumulated_amount ?? 0,
          correctedAmount: correctedPaths[i].accumulated_amount ?? 0,
        }));

        return { ...policy, investment_paths: correctedPaths };
      });

      return rowHasIssue ? { ...product, policies_or_accounts: correctedPolicies } : product;
    });

    if (!rowHasIssue) return row;
    return {
      ...row,
      data: row.data ? { ...row.data, products: correctedProducts } : row.data,
    };
  });

  return {
    valid: issues.length === 0,
    issues,
    correctedData,
  };
}

export function buildIntegrityWarningText(issues: PolicyIntegrityIssue[]): string {
  return issues.map(issue => {
    const header = issue.pathCount === 1
      ? `סכום מסלול (${formatCurrency(issue.actualAmount)}) אינו תואם לסכום הצבירה (${formatCurrency(issue.expectedAmount)})`
      : `${Math.round(issue.coveragePct * 100)}% מהסכום מכוסה במסלולים — ${formatCurrency(issue.actualAmount)} מתוך ${formatCurrency(issue.expectedAmount)}`;

    const pathLines = issue.paths.map(p => {
      const changed = Math.abs(p.originalAmount - p.correctedAmount) > 0.01;
      const correction = changed ? ` ← תוקן ל-${formatCurrency(p.correctedAmount)}` : "";
      return `  • ${p.pathName} (${p.pathCode}): ${formatCurrency(p.originalAmount)}${correction}`;
    });

    return [header, ...pathLines].join("\n");
  }).join("\n\n");
}
